/**
 * Monitores de la computadora de proyección. Chrome y Edge dejan saber qué monitores hay
 * (Window Management API, con permiso una sola vez) para abrir cada salida en el suyo. La
 * elección se guarda en esta computadora: otra computadora tiene otros monitores.
 */

export type Output = 'congregacion' | 'escenario';

export interface Monitor {
  key: string;
  name: string;
  left: number;
  top: number;
  width: number;
  height: number;
  primary: boolean;
}

/** Lo mínimo de la API que se usa (TypeScript todavía no la trae). */
interface ScreenDetailed {
  label: string;
  left: number;
  top: number;
  availLeft: number;
  availTop: number;
  width: number;
  height: number;
  isPrimary: boolean;
  isInternal: boolean;
}
interface ScreenDetails {
  screens: ScreenDetailed[];
}
type WithScreens = Window & { getScreenDetails?: () => Promise<ScreenDetails> };

const STORE = 'keora.pantallas';
const READY = 'keora:pantalla-lista';
const FULL = 'keora:pantalla-completa';

/** Dentro de la app de escritorio «KEORA Multimedia» las pantallas se ponen a pantalla completa solas. */
export function inDesktopApp(): boolean {
  if (typeof window === 'undefined') return false;
  if ('keoraApp' in window) return true;
  try {
    return navigator.userAgent.includes('KeoraProyeccion/');
  } catch {
    return false;
  }
}

export const canPickMonitor = () => typeof window !== 'undefined' && typeof (window as WithScreens).getScreenDetails === 'function';

const toMonitor = (s: ScreenDetailed, i: number): Monitor => ({
  key: `${s.left},${s.top},${s.width}x${s.height}`,
  name: `${s.isInternal ? 'Pantalla de la laptop' : s.label || `Monitor ${i + 1}`}${s.isPrimary ? ' (principal)' : ''} · ${s.width}×${s.height}`,
  left: s.left,
  top: s.top,
  width: s.width,
  height: s.height,
  primary: s.isPrimary,
});

async function details(): Promise<ScreenDetailed[]> {
  const get = (window as WithScreens).getScreenDetails;
  if (!get) return [];
  return (await get.call(window)).screens;
}

/** Pide permiso (la primera vez) y devuelve los monitores conectados. */
export async function listMonitors(): Promise<Monitor[]> {
  return (await details()).map(toMonitor);
}

/** Sin preguntar: ¿ya dio permiso esta computadora? */
export async function monitorsAllowed(): Promise<boolean> {
  try {
    const status = await navigator.permissions.query({ name: 'window-management' as PermissionName });
    return status.state === 'granted';
  } catch {
    return false;
  }
}

/** Lo elegido por salida: antes solo la clave; ahora el monitor completo, para reconocerlo aunque cambie algo. */
export type MonitorChoice = Partial<Record<Output, string | Monitor>>;

/** Nombre del monitor sin la resolución («Monitor 2 · 1920×1080» → «Monitor 2»). */
const baseName = (name: string) => name.split(' · ')[0]!.replace(/ \(principal\)$/, '');

/**
 * El monitor guardado para una salida. Se busca por su clave (posición y resolución); si Windows
 * cambió la resolución o el orden, por su nombre; si era el que no es el principal, por eso. Si
 * todavía no se pudieron leer los monitores, se usa el guardado tal cual.
 */
export function findMonitor(saved: string | Monitor | undefined, monitors: Monitor[]): Monitor | null {
  if (!saved) return null;
  const key = typeof saved === 'string' ? saved : saved.key;
  const exact = monitors.find((m) => m.key === key);
  if (exact) return exact;
  if (typeof saved === 'string') return null;
  if (monitors.length === 0) return saved;
  const byName = monitors.filter((m) => baseName(m.name) === baseName(saved.name));
  if (byName.length === 1) return byName[0]!;
  if (!saved.primary && monitors.length > 1) return monitors.find((m) => !m.primary) ?? null;
  return null;
}

export function loadChoice(): MonitorChoice {
  try {
    return JSON.parse(localStorage.getItem(STORE) ?? '{}') as MonitorChoice;
  } catch {
    return {};
  }
}

export function saveChoice(choice: MonitorChoice) {
  try {
    localStorage.setItem(STORE, JSON.stringify(choice));
  } catch {
    // Sin almacenamiento (ventana privada): se elige de nuevo la próxima vez.
  }
}

/** Cada salida tiene su ventana con nombre fijo: volver a proyectar la reutiliza, no abre otra. */
const windowName = (churchId: string, presentationId: string, output: Output) => `keora-${output}-${churchId}-${presentationId}`;

/** Ventanas abiertas desde esta consola: volver a usarlas no gasta el clic en abrir otra. */
const opened = new Map<string, Window>();

/**
 * Proyecta una salida: abre (o reutiliza) su ventana en el monitor elegido y le presta el clic
 * para ponerse a pantalla completa. Chrome gasta el clic al abrir una ventana nueva, así que la
 * primera vez puede hacer falta un segundo clic: entonces se llama a `needsClick` y el siguiente
 * «Proyectar» (con la ventana ya abierta) la pone a pantalla completa. Sin monitor elegido se abre
 * como pestaña normal. Devuelve false si el navegador bloqueó la ventana.
 */
export function project(url: string, ids: { churchId: string; presentationId: string }, output: Output, monitor: Monitor | null, needsClick: () => void): boolean {
  const name = windowName(ids.churchId, ids.presentationId, output);
  if (!monitor) {
    const tab = window.open(url, name);
    if (tab) opened.set(name, tab);
    return tab !== null;
  }

  const full = (win: Window) => {
    try {
      win.postMessage({ type: FULL }, { targetOrigin: window.location.origin, delegate: 'fullscreen' } as WindowPostMessageOptions);
    } catch {
      // El clic ya se gastó al abrir la ventana: la ventana pide su propio clic y la consola ofrece otro.
      win.postMessage({ type: FULL }, window.location.origin);
      if (!inDesktopApp()) needsClick();
    }
  };

  const known = opened.get(name);
  if (known && !known.closed) {
    try {
      known.moveTo(monitor.left, monitor.top);
    } catch {
      // Sin permiso para moverla: se queda donde está.
    }
    full(known);
    return true;
  }

  const place = `popup,left=${monitor.left},top=${monitor.top},width=${monitor.width},height=${monitor.height}`;
  // Si la ventana ya estaba abierta (por ejemplo, tras recargar la consola) se toma sin recargarla.
  const win = window.open('', name, place);
  if (!win) return false;
  opened.set(name, win);
  let fresh = true;
  try {
    fresh = win.location.href === 'about:blank';
  } catch {
    fresh = true;
  }
  if (fresh) {
    const onReady = (e: MessageEvent) => {
      if (e.source !== win || e.origin !== window.location.origin || (e.data as { type?: string })?.type !== READY) return;
      window.removeEventListener('message', onReady);
      full(win);
    };
    window.addEventListener('message', onReady);
    setTimeout(() => window.removeEventListener('message', onReady), 15_000);
    win.location.href = `${url}?proyectar=1`;
  } else {
    full(win);
  }
  return true;
}

/**
 * Las pantallas abiertas avisan cada segundo por este canal (mismo sitio, misma computadora) que
 * siguen ahí. Así la consola sabe que está proyectando aunque se haya recargado y ya no tenga la
 * ventana en memoria, y puede pedirle a esa misma ventana que se cierre.
 */
const CHANNEL = 'keora-pantallas';
type OutputMessage = { type: 'aqui' | 'cerrar' | 'quien'; churchId: string; presentationId: string; output?: Output };
const lastSeen = new Map<string, number>();
let listening: BroadcastChannel | null = null;

function channel(): BroadcastChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL);
}

/** Consola: empieza a escuchar a las pantallas abiertas (y les pregunta quién está). */
export function watchOutputs(ids: { churchId: string; presentationId: string }): () => void {
  const ch = channel();
  if (!ch) return () => undefined;
  listening = ch;
  ch.onmessage = (e: MessageEvent<OutputMessage>) => {
    const m = e.data;
    if (m?.type === 'aqui' && m.output && m.churchId === ids.churchId && m.presentationId === ids.presentationId) lastSeen.set(windowName(m.churchId, m.presentationId, m.output), Date.now());
  };
  ch.postMessage({ type: 'quien', ...ids } satisfies OutputMessage);
  return () => {
    ch.close();
    if (listening === ch) listening = null;
  };
}

/** Pantalla de salida: avisa que está abierta y se cierra si la consola se lo pide. */
export function announceOutput(ids: { churchId: string; presentationId: string }, output: Output): () => void {
  const ch = channel();
  if (!ch) return () => undefined;
  const here = () => ch.postMessage({ type: 'aqui', ...ids, output } satisfies OutputMessage);
  ch.onmessage = (e: MessageEvent<OutputMessage>) => {
    const m = e.data;
    if (!m || m.churchId !== ids.churchId || m.presentationId !== ids.presentationId) return;
    if (m.type === 'quien') here();
    if (m.type === 'cerrar' && m.output === output) window.close();
  };
  here();
  const t = setInterval(here, 1000);
  return () => {
    clearInterval(t);
    ch.close();
  };
}

/** ¿La ventana de esta salida sigue abierta? (la que abrió esta consola, o una que avisa que está ahí) */
export function isProjecting(ids: { churchId: string; presentationId: string }, output: Output): boolean {
  const name = windowName(ids.churchId, ids.presentationId, output);
  const win = opened.get(name);
  if (win && !win.closed) return true;
  return Date.now() - (lastSeen.get(name) ?? 0) < 2500;
}

/** Deja de proyectar una salida: cierra su ventana (también la que quedó abierta antes de recargar). */
export function stopProjecting(ids: { churchId: string; presentationId: string }, output: Output): void {
  const name = windowName(ids.churchId, ids.presentationId, output);
  opened.get(name)?.close();
  opened.delete(name);
  lastSeen.delete(name);
  const ch = listening ?? channel();
  ch?.postMessage({ type: 'cerrar', ...ids, output } satisfies OutputMessage);
  if (ch && ch !== listening) ch.close();
}

/**
 * Lado de la salida: avisa a la consola que ya cargó y, cuando le cede el clic, se pone a
 * pantalla completa en el monitor elegido.
 */
export function listenForProjection(): () => void {
  if (!window.opener || !new URLSearchParams(window.location.search).has('proyectar')) return () => undefined;
  const onMessage = (e: MessageEvent) => {
    const data = e.data as { type?: string };
    if (e.origin !== window.location.origin || data?.type !== FULL) return;
    void goFullscreen();
  };
  window.addEventListener('message', onMessage);
  // Si el clic prestado no alcanzó, basta un clic en cualquier parte de la ventana.
  const onClick = () => void goFullscreen();
  window.addEventListener('click', onClick);
  try {
    (window.opener as Window).postMessage({ type: READY }, window.location.origin);
  } catch {
    // La consola se cerró: queda el botón de pantalla completa.
  }
  return () => {
    window.removeEventListener('message', onMessage);
    window.removeEventListener('click', onClick);
  };
}

async function goFullscreen() {
  const el = document.documentElement;
  if (document.fullscreenElement) return;
  // Sin esperas antes de pedirla: el clic prestado dura poco.
  await el.requestFullscreen().catch(() => undefined);
}
